import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Alert from "view/components/Alert";
import {activateUser, apiV2, signInWithCustomToken} from "api/firebase";
import {TwoFactorMethodType} from "@evennt-tech/api-v2-external";
import TwoFactorChallenge from "view/components/TwoFactorChallenge";
import {ErrorType} from "ts/api/error";
import React, {useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import Copyright from "view/components/Copyright";
import {APP_PATH} from "view/routes";
import {useVersion} from "store/version/version.store";
import {
  cancelSessionSwitch,
  markSessionSwitchInProgress,
} from "tools/auth/sessionSync";
import {prepareSessionSwitch} from "tools/auth/logoutCleanup";
import {AxiosError} from "axios";
import useStyles from "./style";

const encodePlusSymbol = (value: string) => value.replace(/\+/g, "%2b");

const LoginSSO: React.FC = () => {
  const classes = useStyles();
  const history = useNavigate();
  const {autoCatch, fetch} = useVersion();

  const [error, setError] = useState<string>("");
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [challenge, setChallenge] = useState<{
    pendingToken: string;
    method: TwoFactorMethodType;
    methods: TwoFactorMethodType[];
  } | null>(null);

  const [params] = useSearchParams();

  const signature = params.get("signature");
  const clientReference = params.get("clientReference");
  const timestamp = params.get("timestamp");
  const showId = params.get("showId");

  const completeLogin = async (customToken: string) => {
    const me = await apiV2.user.getMeSafe().catch(() => null);
    if (me) {
      await prepareSessionSwitch();
    } else {
      markSessionSwitchInProgress();
    }
    await signInWithCustomToken(customToken);
    if (showId) {
      await activateUser(showId);
    }
    await apiV2.user.getMe();
    await apiV2.notification.connect();

    setIsSuccess(true);
    history(APP_PATH);
  };

  const handleLoginError = (error: unknown) => {
    // SSO link or sign-in failed — release the cross-tab loader on
    // sibling /login tabs.
    cancelSessionSwitch();
    setIsSuccess(false);

    console.error(error);

    const axiosError = error as AxiosError;

    if (axiosError.response && axiosError?.response?.data?.message) {
      setError(axiosError?.response?.data?.message);
      return;
    }

    const typedError = error as ErrorType;
    if (typedError.message && !isSuccess) {
      setError(typedError.message);
    }
  };

  const finishLogin = async (customToken: string) => {
    try {
      await completeLogin(customToken);
    } catch (error) {
      handleLoginError(error);
    } finally {
      await autoCatch();
      await fetch();
    }
  };

  useEffect(() => {
    const loginSSO = async () => {
      try {
        setError("");
        if (!signature || !clientReference || !timestamp || !showId) {
          throw Error("SSO link parameters weren't set");
        }

        const result = await apiV2.ssoConnection.logIn({
          clientReference,
          signature: encodePlusSymbol(signature),
          showId,
          timestamp,
        });

        if ("twoFactorRequired" in result) {
          setChallenge({
            pendingToken: result.pendingToken,
            method: result.method,
            methods: result.methods,
          });
          return;
        }

        await completeLogin(result.token);
      } catch (error) {
        handleLoginError(error);
      } finally {
        await autoCatch();
        await fetch();
      }
    };

    loginSSO();
  }, [signature, history, clientReference, timestamp, showId]);

  const closePage = () => {
    history(APP_PATH);
  };

  if (challenge) {
    return (
      <TwoFactorChallenge
        pendingToken={challenge.pendingToken}
        method={challenge.method}
        methods={challenge.methods}
        onComplete={finishLogin}
      />
    );
  }

  return (
    <>
      <Box className={classes.content}>
        <Box className={classes.container}>
          {error ? (
            <Paper variant="outlined" className={classes.errorPaper}>
              <Alert severity="error" className={classes.errorElement}>
                {error}
              </Alert>
              <Typography className={classes.errorElement}>
                Oops, there’s an issue connecting to your FFAIR account, please
                contact hi@ffair.io with the error code above.
              </Typography>
              <Button
                className={classes.closeButton}
                variant="contained"
                color="primary"
                endIcon={<CloseIcon />}
                disableElevation
                onClick={closePage}
              >
                Close
              </Button>
            </Paper>
          ) : (
            <Box className={classes.loaderBlock}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
        <Box>
          <Copyright />
        </Box>
      </Box>
    </>
  );
};

export default LoginSSO;
